import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStepEditorContext } from 'src/store/step-editor';
import {
  STEP_EDITOR_CONTENT_OPTION_INITIAL,
  STEP_EDITOR_CONTENT_OPTION_LIBRARY,
  STEP_EDITOR_CONTENT_OPTION_RECORD,
  STEP_EDITOR_CONTENT_OPTION_UPLOAD,
  CONTENT_TYPE_JOURNEY_DOCUMENT,
} from '../constants';
import { StepEditorSection } from './content/section';
import { StepEditorPreviewPanel } from './preview/panel';
import StepEditorContentSearch from 'src/editor/step-editor/content/search';
import { StepEditorContentConfigureStageTypeEnum } from './content/configure/stage-type.enum';

import { LIBRARY_TABS } from 'src/editor/step-editor/content/library/types';
import { Node } from 'src/common/interfaces/node.interface';
import { useEmbedUrlChange } from 'src/editor/step-editor/use-embed-url-change.hook';
import { useEditorContext } from 'src/store/editor';
import { findStepInJourney } from 'src/utils/journey';
import { StepEditorContentPanel } from './content/panel';
import { IconButton } from 'src/common/icon-button';
import { EventBus } from 'src/common/EventBus';
import {
  AddRegularIcon,
  CameraRegularIcon,
  GoogleDriveIntegrationIcon,
  LeftStrongIcon,
  TemplateRegularIcon,
  UploadRegularIcon,
} from 'src/monet/icons';
import { useEditorNavigator } from '../use-editor-navigator.hook';

import { useSubmitStepForm } from 'src/editor/step-editor/use-submit-step-form';
import { Content } from 'src/common/interfaces/content.interface';


interface StepEditorInitialStageProps {
  stepId?: string;
}

export const StepEditorInitialStage = ({ stepId }: StepEditorInitialStageProps) => {
  const {
    url,
    name,
    node_id,
    contentOption,
    searchQuery,
    setSearchQuery,
    currentConfigurationStage,
    setCurrentConfigurationStage,
    setContentId,
    setContentOption,
    setContentType,
    setContentDetails,
    setStateFromNode,
    setLibraryTab,
    setName,
  } = useStepEditorContext();
  const { checkEmbedUrl } = useEmbedUrlChange();
  const [inProgress, setInProgress] = useState(false);
  const { navigateToJourneyEdit, navigateToContentEdit } = useEditorNavigator();
  const { journey, setCurrentStep, currentStep, setNodeToAddAfter, updateNodeInJourney, setCurrentActiveNodeId } =
    useEditorContext();
  const { submitForm } = useSubmitStepForm();

  const showInitialOptions = useMemo(() => contentOption === STEP_EDITOR_CONTENT_OPTION_INITIAL, [contentOption]);

  useEffect(() => {
    if (journey) {
      const currentStep = findStepInJourney(journey, stepId);
      setCurrentStep(currentStep);
    }
  }, [journey, stepId, setCurrentStep]);

  const onEditorSectionClicked = useCallback(
    (section: string) => () => {
      setContentOption(section);
    },
    [setContentOption]
  );

  const onCreateUntitledDocument = useCallback(() => {
    setInProgress(true);
    setContentType(CONTENT_TYPE_JOURNEY_DOCUMENT);
    submitForm({ name: 'Untitled Document', content_type: CONTENT_TYPE_JOURNEY_DOCUMENT }, (nodeResponse: Node) => {
      if (currentStep) {
        updateNodeInJourney(nodeResponse, currentStep);
        setStateFromNode(nodeResponse);
        EventBus.emit('editor:reset-stage', { showInitialStage: false });
        navigateToContentEdit(currentStep?.id, nodeResponse.id);
        setCurrentActiveNodeId(nodeResponse.id);
        setInProgress(false);
      }
    });
  }, [
    setContentType,
    submitForm,
    currentStep,
    updateNodeInJourney,
    setStateFromNode,
    navigateToContentEdit,
    setCurrentActiveNodeId,
  ]);

  const isEditingStep = useMemo(() => node_id !== null, [node_id]);

  const onBackButtonClicked = useCallback(() => {
    setNodeToAddAfter(null);
    if (!isEditingStep) {
      navigateToJourneyEdit();
    } else {
      EventBus.emit('editor:reset-stage', { showInitialStage: false });
    }
  }, [isEditingStep, navigateToJourneyEdit]);

  const onGoogleDriveClicked = useCallback(() => {
    setContentOption(STEP_EDITOR_CONTENT_OPTION_LIBRARY);
    const libraryTab = LIBRARY_TABS.find((tab) => tab.type === 'integrations');
    if (libraryTab) {
      setLibraryTab(libraryTab.type);
    }
  }, [setContentOption, setLibraryTab]);

  const onEmbedUrlChange = useCallback(
    (newUrl: string) => {
      // abort if there is no url change
      if (newUrl === url) {
        return;
      }
      checkEmbedUrl(newUrl);
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
    },
    [url, checkEmbedUrl, setCurrentConfigurationStage]
  );

  // const showContentCreation = useMemo(() => {
  //   if (currentOrganization.feature_flags.content_creation_enabled) return true;

  //   return false;
  // }, [currentOrganization]);

  const renderPanelContent = useCallback(() => {
    if (showInitialOptions) {
      return (
        <>
          {!searchQuery && (
            <div className='flex flex-col !mt-4'>
              <StepEditorSection
                onClick={onCreateUntitledDocument}
                wrapperClassName='py-md'
                title='Write document'
                subTitle='Write a rich text document'
                icon={<AddRegularIcon />}
                disabled={inProgress}
              />
              <StepEditorSection
                onClick={onEditorSectionClicked(STEP_EDITOR_CONTENT_OPTION_RECORD)}
                wrapperClassName='py-md'
                title='Record a video'
                subTitle='Record a video in browser'
                icon={<CameraRegularIcon />}
              />
              <StepEditorSection
                onClick={onEditorSectionClicked(STEP_EDITOR_CONTENT_OPTION_UPLOAD)}
                wrapperClassName='py-md'
                title='Upload a file'
                subTitle='Add any file such as PDF, video, image'
                icon={<UploadRegularIcon />}
              />
              <StepEditorSection
                wrapperClassName='py-md'
                onClick={onEditorSectionClicked(STEP_EDITOR_CONTENT_OPTION_LIBRARY)}
                title='Use existing content'
                subTitle='Find content you have in your content library'
                icon={<TemplateRegularIcon />}
              />
              <StepEditorSection
                hideBorderBottom
                wrapperClassName='py-md'
                onClick={onGoogleDriveClicked}
                title='Connect Google Drive'
                subTitle='Easily add content from Google Drive'
                icon={<GoogleDriveIntegrationIcon className='w-5 h-5' />}
              />
            </div>
          )}
        </>
      );
    }

    return (
      <StepEditorContentPanel
        editMode={isEditingStep}
        activeStage={currentConfigurationStage}
        onEmbedUrlChange={onEmbedUrlChange}
      />
    );
  }, [
    isEditingStep,
    showInitialOptions,
    currentConfigurationStage,
    onCreateUntitledDocument,
    onEmbedUrlChange,
    searchQuery,
    onEditorSectionClicked,
  ]);

  return (
    <>
      <div
        className={classNames(
          'absolute pitch-onboarding-customize-links left-0 top-0 bottom-0 z-20 transition-all bg-bedrock-white py-lg px-md h-screen',
          contentOption === STEP_EDITOR_CONTENT_OPTION_LIBRARY
            ? 'w-[656px] border-r-2 border-bedrock-light-gray'
            : 'w-[368px]'
        )}
      >
        <div className='flex flex-col space-y-lg h-full overflow-y-auto'>
          {showInitialOptions && (
            <>
              <IconButton onClick={onBackButtonClicked}>
                <LeftStrongIcon />
              </IconButton>
              <StepEditorContentSearch
                onUrlChange={onEmbedUrlChange}
                onChooseExisting={(content: Content) => {
                  setContentDetails(content);
                  if (!name) {
                    setName(content.name);
                  }
                  setContentId(content.id);
                  setSearchQuery('');
                  setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
                }}
              />
            </>
          )}
          {renderPanelContent()}
        </div>
      </div>
      <div className='flex w-full h-screen'>
        <StepEditorPreviewPanel />
      </div>
    </>
  );
};
